import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  MarketGasPrice,
  MarketGasPriceCreateBody,
  MarketGasPriceOnDateArgs,
  MarketGasPriceUpdateBody,
} from './types';

export const marketGasPricesApi = createApi({
  reducerPath: 'marketGasPricesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/market-gas-prices`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['MarketGasPrice', 'MarketGasPrices'],
  endpoints: builder => ({
    getMarketGasPricesI: builder.infiniteQuery<
      MarketGasPrice[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MarketGasPrice', 'MarketGasPrices'],
    }),
    getMarketGasPriceById: builder.query<MarketGasPrice, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'MarketGasPrice', id }],
    }),
    getMarketGasPriceOnDate: builder.query<
      number | null,
      MarketGasPriceOnDateArgs
    >({
      query: ({ typeId, date }) => ({
        url: `/`,
        params: {
          filter: `typeId=${typeId},date=${date}`,
          page: 1,
          pageSize: 1,
        },
      }),
      transformResponse: (items: MarketGasPrice[]) => items[0]?.price ?? null,
      providesTags: () => ['MarketGasPrices'],
    }),
    createMarketGasPrice: builder.mutation<string, MarketGasPriceCreateBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['MarketGasPrices'],
    }),
    updateMarketGasPrice: builder.mutation<
      void,
      { id: string; body: MarketGasPriceUpdateBody }
    >({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'MarketGasPrices',
        { type: 'MarketGasPrice', id },
      ],
    }),
  }),
});

export const {
  useGetMarketGasPriceByIdQuery,
  useGetMarketGasPriceOnDateQuery,
  useCreateMarketGasPriceMutation,
  useUpdateMarketGasPriceMutation,
} = marketGasPricesApi;

export const useGetMarketGasPricesInfiniteQuery =
  normalizeInfiniteQueryData<MarketGasPrice>(
    marketGasPricesApi.useGetMarketGasPricesIInfiniteQuery,
  );
